"use client";

import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
} from "@mui/icons-material";

import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../firebaseConfig";

function formatDate(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export default function CommentList() {
  const [comentarios, setComentarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedComentario, setSelectedComentario] = useState(null);
  const [filterStars, setFilterStars] = useState(0); // Nuevo estado para el filtro de estrellas

  useEffect(() => {
    const q = query(collection(db, "pqyr"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const comentariosArray = [];
      querySnapshot.forEach((doc) => {
        comentariosArray.push({ ...doc.data(), id: doc.id });
      });
      setComentarios(comentariosArray);
    });

    return () => unsubscribe();
  }, []);

  const filteredComentarios = comentarios.filter((comentario) => {
    const nombre = comentario.name ? comentario.name.toLowerCase() : "";
    const clinica = comentario.selectedClinic
      ? comentario.selectedClinic.toLowerCase()
      : "";
    const comentarioTexto = comentario.comment
      ? comentario.comment.toLowerCase()
      : "";
    const email = comentario.email ? comentario.email.toLowerCase() : "";
    const telefono = comentario.contactNumber
      ? comentario.contactNumber.toLowerCase()
      : "";
    const search = searchTerm.toLowerCase();

    // Filtro por número de estrellas
    if (filterStars > 0 && comentario.rating !== filterStars) {
      return false;
    }

    return (
      nombre.includes(search) ||
      clinica.includes(search) ||
      comentarioTexto.includes(search) ||
      email.includes(search) ||
      telefono.includes(search)
    );
  });

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "¡No podrás revertir esto!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, bórralo!",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "pqyr", id));
        Swal.fire("¡Eliminado!", "El comentario ha sido eliminado.", "success");
      } catch (error) {
        console.error("Error al eliminar el comentario:", error);
        Swal.fire(
          "Error!",
          "Hubo un problema al eliminar el comentario.",
          "error"
        );
      }
    }
  };

  const handleOpenModal = (comentario) => {
    setSelectedComentario(comentario);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedComentario(null);
  };

  // Función para renderizar las estrellas
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <StarIcon
          key={i}
          style={{ color: i < rating ? "#ffc107" : "#e4e5e9" }}
        />
      );
    }
    return stars;
  };

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        Lista de Comentarios
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
        <TextField
          label="Buscar comentario"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ marginRight: 2 }}
        />

        <FormControl variant="outlined">
          <InputLabel id="filter-stars-label">Filtrar por Estrellas</InputLabel>
          <Select
            labelId="filter-stars-label"
            id="filter-stars"
            value={filterStars}
            onChange={(e) => setFilterStars(e.target.value)}
            label="Filtrar por Estrellas"
          >
            <MenuItem value={0}>Todas</MenuItem>
            <MenuItem value={1}>1 Estrella</MenuItem>
            <MenuItem value={2}>2 Estrellas</MenuItem>
            <MenuItem value={3}>3 Estrellas</MenuItem>
            <MenuItem value={4}>4 Estrellas</MenuItem>
            <MenuItem value={5}>5 Estrellas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ marginTop: 2 }}>
        <Table aria-label="tabla de comentarios">
          <TableHead>
            <TableRow>
              <TableCell>Nº</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Clínica</TableCell>
              <TableCell>Comentario</TableCell>
              <TableCell>Calificación</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredComentarios.map((comentario, index) => (
              <TableRow key={comentario.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{comentario.name}</TableCell>
                <TableCell>{comentario.contactNumber}</TableCell>
                <TableCell>{comentario.selectedClinic}</TableCell>
                <TableCell>
                  {comentario.comment.length > 50
                    ? `${comentario.comment.substring(0, 50)}...`
                    : comentario.comment}
                </TableCell>
                <TableCell>{renderStars(comentario.rating)}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleOpenModal(comentario)}
                    aria-label="ver"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(comentario.id)}
                    aria-label="eliminar"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal para ver el comentario completo */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Detalles del Comentario</DialogTitle>
        <DialogContent>
          {selectedComentario && (
            <Box>
              <Typography gutterBottom>
                <strong>Nombre:</strong> {selectedComentario.name}
              </Typography>
              <Typography gutterBottom>
                <strong>Teléfono:</strong> {selectedComentario.contactNumber}
              </Typography>
              <Typography gutterBottom>
                <strong>Email:</strong> {selectedComentario.email}
              </Typography>
              <Typography gutterBottom>
                <strong>Clínica:</strong> {selectedComentario.selectedClinic}
              </Typography>
              <Typography gutterBottom>
                <strong>Comentario:</strong> {selectedComentario.comment}
              </Typography>
              <Typography gutterBottom>
                <strong>Calificación:</strong>
                {renderStars(selectedComentario.rating)}
              </Typography>
              <Typography gutterBottom>
                <strong>Fecha:</strong>{" "}
                {formatDate(selectedComentario.createdAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}